import React, { useState, useEffect } from 'react';
import { Artist } from './types';
import Button from '../ui/Button';

interface MissingPhotosAlertProps {
  artists: Artist[];
  onDismiss: (permanent?: boolean) => void;
  onViewArtists?: () => void;
}

const MissingPhotosAlert: React.FC<MissingPhotosAlertProps> = ({ 
  artists, 
  onDismiss, 
  onViewArtists 
}) => {
  const [isDismissing, setIsDismissing] = useState(false);

  // Lock background page scroll while the modal is mounted, restoring the
  // previous value on unmount so we don't clobber another lock.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const handleDismissPermanently = async () => {
    setIsDismissing(true);
    try {
      // Set localStorage
      localStorage.setItem('missingPhotosAlertDismissed', 'true');
      onDismiss(true);
    } catch (error) {
      console.error('Error dismissing alert:', error);
    } finally {
      setIsDismissing(false);
    }
  };

  const handleRemindLater = () => {
    onDismiss(false);
  };

  const handleViewArtists = () => {
    if (onViewArtists) {
      onViewArtists();
    }
    onDismiss(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl ring-1 ring-black/5 animate-modalIn">
        {/* Header — clean white with amber warning badge */}
        <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-warning-100">
              <svg className="w-6 h-6 text-warning-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </span>
            <div>
              <h3 className="text-lg font-semibold leading-tight text-black">Artists Missing Photos</h3>
              <p className="text-xs text-neutral-500">Add photos to complete your roster</p>
            </div>
          </div>
          <button
            onClick={handleRemindLater}
            className="p-1.5 transition-colors border border-transparent rounded-lg text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
            aria-label="Close"
            disabled={isDismissing}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-4 text-sm text-neutral-600">
            <span className="font-semibold text-neutral-900">{artists.length}</span>{' '}
            {artists.length === 1 ? 'artist' : 'artists'} in your roster {artists.length === 1 ? 'is' : 'are'} missing profile photos.
          </p>

          {/* Artists List — first 5, then a static "and N more" line (no scroll) */}
          <div className="mb-6 overflow-hidden border rounded-xl border-neutral-200 bg-neutral-50">
            <ul className="divide-y divide-neutral-200">
              {artists.slice(0, 5).map((artist) => (
                <li key={artist.id} className="flex items-center px-4 py-2.5 text-sm text-neutral-700">
                  <span className="w-1.5 h-1.5 mr-3 rounded-full bg-warning-500 shrink-0"></span>
                  {artist.artistName}
                </li>
              ))}
              {artists.length > 5 && (
                <li className="px-4 py-2.5 text-sm italic text-neutral-500">
                  ...and {artists.length - 5} more
                </li>
              )}
            </ul>
          </div>

          {/* Actions — stacked full-width on phones (the three labels need ~395px,
            more than a phone gives), one row from sm up. On the row, the two real
            choices grow to fill the space; the permanent dismissal stays sized to
            its own text so it never gets squeezed into wrapping/overflow. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              onClick={handleViewArtists}
              className="flex-1 px-3 whitespace-nowrap"
              disabled={isDismissing}
            >
              View Artists
            </Button>
            <Button
              variant="secondary"
              onClick={handleRemindLater}
              className="flex-1 px-3 whitespace-nowrap"
              disabled={isDismissing}
            >
              Remind Later
            </Button>
            {/* Lowest emphasis on purpose: the permanent opt-out shouldn't
                compete with the two actions above it. */}
            <Button
              variant="ghost"
              onClick={handleDismissPermanently}
              className="flex-none px-3 bg-transparent text-neutral-500 whitespace-nowrap hover:bg-neutral-100 hover:text-neutral-700"
              disabled={isDismissing}
            >
              {isDismissing ? 'Dismissing...' : 'Dismiss Permanently'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissingPhotosAlert;